import { databaseService } from '@/services/databaseService';
import { sha512Hex } from './PendingOperationTerminalization';
import { assertPendingSyncSessionCurrent, createPendingSyncSupabaseClient, type PendingSyncSessionSnapshot } from './PendingSyncSession';

/** Admissão e resultado lógico são recibos distintos. Nenhum deles autoriza apagar bytes no cliente. */
export async function reconcileChecklistMediaRetention(snapshot: PendingSyncSessionSnapshot) {
  const rows = await databaseService.listItems<Record<string, any>>('deferred_media_cleanup');
  const archives = await databaseService.listItems<Record<string, any>>('accounted_pending_operations');
  const client = createPendingSyncSupabaseClient(snapshot);
  let received = 0; let linked = 0;
  const held: Array<{ id: string; reason: string }> = [];
  for (const row of rows.filter(item => item.empresa_id === snapshot.empresaId)) {
    await assertPendingSyncSessionCurrent(snapshot);
    try {
      const receiptId = `retention:${row.id}`;
      const digest = sha512Hex(row.payload);
      const evidence = JSON.parse(row.payload);
      const prior = await databaseService.readItem<Record<string, any>>('sync_conflicts', receiptId);
      let receipt;
      if (prior?.payload_sha512 === digest && prior.reason === 'media_retention_receipt') {
        receipt = JSON.parse(prior.recovery_id);
      } else {
        if (received >= 30) continue;
        const { data, error } = await client.rpc('accept_checklist_media_retention_v1', {
          p_origin_key: row.id, p_checklist_id: row.checklist_id,
          p_original_author: row.user_id === 'unknown' ? null : row.user_id,
          p_evidence: evidence, p_source_json: row.payload,
        });
        await assertPendingSyncSessionCurrent(snapshot);
        if (error) {
          if (error.code === 'PGRST202' || error.code === '42883') return { received, linked, held, backendAvailable: false };
          throw error;
        }
        if (!data?.id || data.state !== 'received' || data.physical_deleted !== false || data.empresa_id !== row.empresa_id ||
            data.origin_key !== row.id || data.checklist_id !== row.checklist_id || data.source_sha512 !== digest ||
            !/^[0-9a-f]{128}$/.test(data.evidence_sha512)) throw Error('retention_receipt_invalid');
        const current = await databaseService.readItem<Record<string, any>>('deferred_media_cleanup', row.id);
        if (current?.payload !== row.payload) continue;
        const record = { id: receiptId, empresa_id: row.empresa_id, user_id: row.user_id,
          collection: 'deferred_media_cleanup', item_id: row.checklist_id, reason: 'media_retention_receipt',
          status: 'resolved', payload: row.payload, payload_sha512: digest,
          recovery_id: JSON.stringify(data), created_at: Date.now(), updated_at: Date.now() };
        if (prior && !await databaseService.updateItemIfUnchanged('sync_conflicts', receiptId, record, prior, true)) continue;
        if (!prior) await databaseService.createItem('sync_conflicts', record, true);
        receipt = data; received++;
      }
      if (!evidence.next?.id || linked >= 30) continue;
      for (const archive of archives.filter(item => item.empresa_id === row.empresa_id && item.item_id === row.checklist_id && item.accounting_state === 'server_applied')) {
        let outcome;
        try { outcome = JSON.parse(archive.data); } catch { continue; }
        if (outcome.payload?.id !== evidence.next.id || !outcome.idempotency_key) continue;
        const proofId = `retention-result:${row.id}:${archive.id}`;
        if (await databaseService.readItem('sync_conflicts', proofId)) continue;
        await assertPendingSyncSessionCurrent(snapshot);
        const result = await client.rpc('link_checklist_media_retention_result_v1', { p_intent_id: receipt.id, p_idempotency_key: outcome.idempotency_key });
        await assertPendingSyncSessionCurrent(snapshot);
        // Resultado ultrapassado ou divergente permanece em revisão; não vira autorização de limpeza.
        if (result.error) continue;
        if (result.data?.state !== 'logical_confirmed' || result.data.intent_id !== receipt.id || result.data.idempotency_key !== outcome.idempotency_key) throw Error('retention_result_invalid');
        await databaseService.createItem('sync_conflicts', { id: proofId, empresa_id: row.empresa_id, user_id: row.user_id,
          collection: 'deferred_media_cleanup', item_id: row.checklist_id, reason: 'media_retention_logical_receipt', status: 'resolved',
          payload: JSON.stringify(result.data), payload_sha512: sha512Hex(JSON.stringify(result.data)), created_at: Date.now(), updated_at: Date.now() }, true);
        linked++; break;
      }
    } catch (error) {
      // Uma evidência ilegível permanece no cofre; não impede admitir outra evidência válida.
      await assertPendingSyncSessionCurrent(snapshot);
      const reason = error instanceof SyntaxError ? 'retention_invalid_json' : (error as any)?.message;
      if (!['retention_invalid_json', 'media_evidence_too_deep', 'media_path_not_canonical',
        'retention_invalid_scope', 'source_json_mismatch', 'retention_idempotency_mismatch',
        'retention_foreign_object', 'media_already_fenced', 'retention_release_pending', 'retention_rollout_disabled'].includes(reason)) throw error;
      held.push({ id: row.id, reason });
    }
  }
  return { received, linked, held, backendAvailable: true };
}
